import { useState, useEffect, useCallback } from 'react';
import type {
  DashboardMetrics,
  ChartData,
  TenantSummary,
} from '@yawi/types';
import { useServices } from '@/app/providers';

interface DashboardState {
  metrics: DashboardMetrics | null;
  revenueChart: ChartData | null;
  tenantGrowthChart: ChartData | null;
  recentTenants: TenantSummary[];
  isLoading: boolean;
  error: string | null;
}

// Mock data for development while API is not ready
const mockMetrics: DashboardMetrics = {
  mrr: 24500,
  mrrChange: 12.5,
  totalTenants: 127,
  activeTenants: 118,
  churnedTenants: 9,
  tenantChange: 8.2,
  apiRequestsThisMonth: 1250000,
  apiRequestsChange: 15.3,
  totalStorageBytes: 52428800000, // 50GB
  storageChange: 5.1,
};

const mockRevenueChart: ChartData = {
  labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  datasets: [
    {
      name: 'Revenue',
      data: [18000, 19500, 20100, 21000, 21500, 22300, 22800, 23200, 23800, 24100, 24300, 24500],
    },
    {
      name: 'New MRR',
      data: [2000, 1500, 600, 900, 500, 800, 500, 400, 600, 300, 200, 200],
    },
  ],
};

const mockTenantGrowthChart: ChartData = {
  labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  datasets: [
    {
      name: 'New Tenants',
      data: [12, 15, 8, 10, 14, 11, 9, 13, 16, 10, 8, 11],
    },
    {
      name: 'Churned',
      data: [2, 1, 3, 2, 1, 2, 1, 2, 1, 3, 1, 2],
    },
  ],
};

const mockRecentTenants: TenantSummary[] = [
  { id: '1', name: 'Residencial Aurora', slug: 'aurora', plan: 'professional', status: 'active', createdAt: '2024-01-15', mrr: 299 },
  { id: '2', name: 'Torres del Valle', slug: 'torres-valle', plan: 'enterprise', status: 'active', createdAt: '2024-01-14', mrr: 599 },
  { id: '3', name: 'Lomas Verdes', slug: 'lomas-verdes', plan: 'starter', status: 'pending', createdAt: '2024-01-13', mrr: 99 },
  { id: '4', name: 'Bosques del Sur', slug: 'bosques-sur', plan: 'professional', status: 'active', createdAt: '2024-01-12', mrr: 299 },
  { id: '5', name: 'Vista Hermosa', slug: 'vista-hermosa', plan: 'starter', status: 'active', createdAt: '2024-01-11', mrr: 99 },
];

export function useDashboard() {
  const { dashboardRepository } = useServices();
  const [state, setState] = useState<DashboardState>({
    metrics: null,
    revenueChart: null,
    tenantGrowthChart: null,
    recentTenants: [],
    isLoading: true,
    error: null,
  });

  const fetchDashboardData = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // Try to fetch from API, fallback to mock data
      if (dashboardRepository) {
        const [metrics, revenueChart, tenantGrowthChart, recentTenants] = await Promise.all([
          dashboardRepository.getMetrics(),
          dashboardRepository.getRevenueChart(12),
          dashboardRepository.getTenantGrowthChart(12),
          dashboardRepository.getRecentTenants(5),
        ]);

        setState({
          metrics,
          revenueChart,
          tenantGrowthChart,
          recentTenants,
          isLoading: false,
          error: null,
        });
      } else {
        // Use mock data for development
        await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate loading
        setState({
          metrics: mockMetrics,
          revenueChart: mockRevenueChart,
          tenantGrowthChart: mockTenantGrowthChart,
          recentTenants: mockRecentTenants,
          isLoading: false,
          error: null,
        });
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      // Fallback to mock data on error
      setState({
        metrics: mockMetrics,
        revenueChart: mockRevenueChart,
        tenantGrowthChart: mockTenantGrowthChart,
        recentTenants: mockRecentTenants,
        isLoading: false,
        error: null,
      });
    }
  }, [dashboardRepository]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    ...state,
    refresh: fetchDashboardData,
  };
}
